import Box from '@/components/UI/box/Box'
import styles from './GetVerificationCode.module.scss'
import Typography from '@/components/UI/typography/Typography'
import Button from '@/components/UI/button/Button'
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react'
import useLogin from '../../hooks/useLogin'
import TextField from '@/components/UI/textField/TextField'
import CountDownTimer from './countDownTimer/CountDownTimer'
import { digitsFaToEn } from '@persian-tools/persian-tools'
import { useDispatch, useSelector } from 'react-redux'
import { loginOtp, verifyOtp } from '@/redux/store/actions/auth'
import { useRouter } from 'next/router'
import { useIsWhiteLabel } from '@/components/app/providers/whiteLabelProvider/WhiteLabelProvider'

const GetVerificationCode = () => {
  const router = useRouter()
  const dispatch = useDispatch()
  const user_info = useSelector(state => state.bootstrapData?.data?.user_info)
  const {
    handleChangeStep,
    phoneNumberDefault,
    handleSetOTP,
    loginCallback,
    onClose,
  } = useLogin()
  const [verificationCode, setVerificationCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const ac = useRef(new AbortController())
  const isLogin = user_info?.uid
  const isWhiteLabel = useIsWhiteLabel()
  useEffect(() => {
    if (isLogin) {
      loginCallback({ isHost: user_info?.is_host })
      if (user_info?.is_host && !router.query.next_url && !isWhiteLabel) {
        handleChangeStep('SHOWHOSTPANELCTA')
      } else {
        onClose && onClose()
      }
    } else {
      if (inputRef.current) {
        // handle Iphone keyboard popup
        requestAnimationFrame(() => {
          inputRef.current?.focus()
        })
      }
      handleReadSmsCode()
    }
    return () => {
      ac?.current?.abort()
    }
  }, [error, isLogin])

  const handleReadSmsCode = () => {
    if ('OTPCredential' in window) {
      // @ts-ignore
      navigator.credentials
        .get({
          // @ts-ignore
          otp: {
            transport: ['sms'],
          },
          signal: ac.current.signal,
        })
        .then(t => {
          // @ts-ignore
          const otpCode = t.code
          setVerificationCode(otpCode)
          handleSubmit(undefined, otpCode)
        })
        .catch(() => {
          ac.current.abort()
          console.warn('WebOTP API not supported.')
        })
    }
    return ac
  }

  const handleReset = () => {
    setVerificationCode('')
    setError('')
    inputRef.current?.select()
    dispatch(
      loginOtp(phoneNumberDefault, ({ message, status }) => {
        setLoading(false)
        if (status === 'SUCCESS') {
          setError('')
        } else {
          setError(message)
        }
      }),
    )
  }

  const handleSubmit = (
    e?: FormEvent<HTMLFormElement>,
    otp: string = verificationCode,
  ) => {
    if (e) {
      e.preventDefault()
    }

    setLoading(true)
    setError('')

    dispatch(
      verifyOtp(
        { mobile: phoneNumberDefault || '', code: otp },
        ({ status, message, type }) => {
          ac.current.abort()
          if (status === 'SUCCESS') {
            if (type === 'register') {
              handleSetOTP(otp)
              handleChangeStep('GETINFORMATION')
            }
          } else {
            setLoading(false)
            if (message) {
              setError(message)
            }
            inputRef.current?.select()
          }
        },
      ),
    )
  }

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    let otp = verificationCode
    if (e.target.value.match(/^$|^[0-9\u06F0-\u06F9]+$/)) {
      otp = digitsFaToEn(e.target.value)
      setError('')
    }
    if (String(otp).length <= 4) {
      setVerificationCode(otp)
    }
    if (String(otp).length === 4) {
      handleSubmit(undefined, otp)
    }
  }
  const handleClean = () => {
    setVerificationCode('')
  }
  const handleChangePhoneNumber = () => {
    handleChangeStep('GETPHONENUMBER')
  }

  return (
    <Box
      className={`${styles['get-verification-code']} ${styles['slide-in-right']}`}
    >
      <Box className={styles['countdown-text']}>
        <Typography variant="bodyS">
          کد به شماره
          <strong>{` ${phoneNumberDefault} `}</strong>
          پیامک شد.
        </Typography>
        <CountDownTimer onReset={handleReset} />
      </Box>
      <Typography
        color={'neutral_main'}
        variant="bodyM"
        component="p"
        className={styles['info-text']}
      >
        کد ۴ رقمی در پیامک دریافتی را وارد کنید:
      </Typography>

      <form className={styles['form']} onSubmit={handleSubmit}>
        <TextField
          label={'کد ورود'}
          value={verificationCode}
          onChange={handleInput}
          type="tel"
          ref={inputRef}
          autoComplete="one-time-code"
          error={!!error}
          description={error || ''}
          disabled={loading}
          onClear={handleClean}
          className={styles['input-phone']}
        />
        <Button
          type="submit"
          format="primary"
          size={'large'}
          loading={loading}
          disabled={
            !verificationCode ||
            verificationCode.toString().length < 4 ||
            !error
          }
        >
          ادامه
        </Button>
      </form>
      <Box className={styles['change-phone']}>
        <Typography variant="bodyS">شماره همراه صحیح نیست؟</Typography>
        <Typography
          variant="bodyS"
          color="primary_main"
          onClick={handleChangePhoneNumber}
          className={styles['change-phone-btn']}
        >
          تغییر شماره
        </Typography>
      </Box>
    </Box>
  )
}

export default GetVerificationCode
